import React, { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";

const round2 = (n) => Math.round(Number(n) * 100) / 100;
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import {
  Store, Plus, Search, MapPin, User, Clock, Percent, ShieldCheck,
  Pencil, Trash2, Save, Copy, X, RefreshCw, KeyRound, IndianRupee, Package,
} from "lucide-react";

const emptyForm = {
  code: "",
  name: "",
  address_line1: "",
  address_line2: "",
  city: "",
  state: "",
  pincode: "",
  gstin: "",
  phone: "",
  email: "",
  store_manager_name: "",
  store_manager_phone: "",
  opening_hours: "",
  pos_terminal_count: "",
  default_gst_rate: 5,
  location_lat: "",
  location_lng: "",
  notes: "",
  is_active: true,
  ownership_type: "owned",
  owner_pct: 30,
  franchisee_pct: 70,
  auto_create_user: false,
  auto_create_role: "branch_manager",
  admin_name: "",
  admin_email: "",
  team: [
    { role: "showroom_admin",         role_label: "Showroom Admin",  enabled: true,  name: "", email: "", phone: "", password: "" },
    { role: "showroom_sales_manager", role_label: "Sales Manager",   enabled: false, name: "", email: "", phone: "", password: "" },
    { role: "cashier",                role_label: "Cashier",         enabled: false, name: "", email: "", phone: "", password: "" },
  ],
};

export default function ShowroomsPage() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [showActiveOnly, setShowActiveOnly] = useState(false);
  const [editing, setEditing] = useState(null);
  const [newCredential, setNewCredential] = useState(null);
  const [catalogFor, setCatalogFor] = useState(null); // showroom whose catalog we're editing
  const [teamFor, setTeamFor] = useState(null); // showroom whose team we're managing

  const load = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (search.trim()) params.set("search", search.trim());
      if (showActiveOnly) params.set("active", "true");
      const r = await api.get(`/showrooms${params.toString() ? `?${params.toString()}` : ""}`);
      setRows(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load showrooms");
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [showActiveOnly]);

  const stats = useMemo(() => ({
    total: rows.length,
    active: rows.filter((r) => r.is_active !== false).length,
    users: rows.reduce((s, r) => s + (r.users_count || 0), 0),
    todaySales: rows.reduce((s, r) => s + (Number(r.today_sales_amount) || 0), 0),
  }), [rows]);

  const toggleActive = async (s) => {
    try {
      await api.patch(`/showrooms/${s.id}`, { is_active: !s.is_active });
      toast.success(`${s.name} ${!s.is_active ? "activated" : "deactivated"}`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const remove = async (s) => {
    if (!window.confirm(`Delete "${s.name}"? This cannot be undone.`)) return;
    try {
      await api.delete(`/showrooms/${s.id}`);
      toast.success("Showroom deleted");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Cannot delete"); }
  };

  return (
    <div className="space-y-6" data-testid="showrooms-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <div className="uc-label">Retail · Showroom Master</div>
          <h1 className="font-[Outfit] text-3xl sm:text-4xl font-semibold tracking-tight mt-1">Retail Showrooms</h1>
          <p className="text-sm text-[#8B7355] mt-1">
            Each retail store operates independently — its own login, its own product catalog, its own pricing.
            Super Admin only.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={load} disabled={loading} data-testid="sr-refresh">
            <RefreshCw className={`w-3.5 h-3.5 mr-1 ${loading ? "animate-spin" : ""}`} /> Refresh
          </Button>
          <Button
            onClick={() => setEditing({})}
            className="bg-[#C05621] text-white hover:bg-[#a04519]"
            data-testid="sr-new-btn"
          >
            <Plus className="w-4 h-4 mr-1" /> New Showroom
          </Button>
        </div>
      </div>

      {/* Stat tiles */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <StatTile label="Total Showrooms" value={stats.total} tone="neutral" icon={Store} />
        <StatTile label="Active" value={stats.active} tone="emerald" icon={ShieldCheck} />
        <StatTile label="Staff Logins" value={stats.users} tone="blue" icon={User} />
        <StatTile label="Today's Revenue" value={`₹${stats.todaySales.toLocaleString(undefined, { maximumFractionDigits: 0 })}`} tone="amber" icon={IndianRupee} />
      </div>

      {/* Filters */}
      <form
        onSubmit={(e) => { e.preventDefault(); load(); }}
        className="flex flex-wrap items-end gap-2 p-3 bg-white border border-[#EAE5D9] rounded-md"
      >
        <div className="flex-1 min-w-[240px]">
          <Label className="text-[10px] uppercase text-[#8B7355]">Search</Label>
          <div className="relative">
            <Search className="w-3.5 h-3.5 absolute left-2 top-2.5 text-[#8B7355]" />
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Name, code, or city…"
              className="pl-7"
              data-testid="sr-search"
            />
          </div>
        </div>
        <label className="flex items-center gap-2 text-xs cursor-pointer px-2 py-2">
          <Switch checked={showActiveOnly} onCheckedChange={setShowActiveOnly} data-testid="sr-active-only" />
          <span>Active only</span>
        </label>
        <Button type="submit" variant="outline" size="sm"><Search className="w-3.5 h-3.5 mr-1" /> Search</Button>
      </form>

      {/* Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="sr-grid">
        {rows.map((s) => (
          <ShowroomCard
            key={s.id}
            s={s}
            onEdit={() => setEditing(s)}
            onToggle={() => toggleActive(s)}
            onDelete={() => remove(s)}
            onCatalog={() => setCatalogFor(s)}
            onTeam={() => setTeamFor(s)}
          />
        ))}
        {rows.length === 0 && !loading && (
          <div className="col-span-full text-center py-16 bg-white border border-dashed border-[#EAE5D9] rounded-lg">
            <Store className="w-10 h-10 mx-auto text-[#8B7355] opacity-40 mb-2" />
            <div className="text-sm text-[#8B7355]">
              No retail showrooms yet. Click <b>New Showroom</b> to add your first store.
            </div>
          </div>
        )}
      </div>

      {editing !== null && (
        <ShowroomFormDialog
          initial={editing.id ? editing : null}
          onClose={() => setEditing(null)}
          onSaved={(res) => {
            setEditing(null);
            load();
            // Multi-user creation flow (wizard-style Create Store + Team)
            if (res?.multi_creds && res.multi_creds.length > 0) {
              setNewCredential({
                multi: true,
                showroom_name: res.showroom?.name,
                store_code: res.showroom?.store_code,
                team: res.multi_creds,
              });
            } else if (res?.user_creation?.created) {
              setNewCredential({
                showroom_name: res.showroom?.name,
                email: res.user_creation.email,
                password: res.user_creation.temp_password,
                name: res.user_creation.name,
                role: res.user_creation.role,
              });
            } else if (res?.user_creation && !res.user_creation.created) {
              toast.info(res.user_creation.reason);
            }
          }}
        />
      )}

      {newCredential && (
        <NewUserCredsDialog
          creds={newCredential}
          onClose={() => setNewCredential(null)}
        />
      )}

      {catalogFor && (
        <ShowroomCatalogDialog
          showroom={catalogFor}
          onClose={() => setCatalogFor(null)}
        />
      )}

      {teamFor && (
        <ShowroomTeamDialog
          showroom={teamFor}
          onClose={() => setTeamFor(null)}
          onCredentials={(creds) => setNewCredential(creds)}
          onChanged={load}
        />
      )}
    </div>
  );
}

function StatTile({ label, value, tone, icon: Icon }) {
  const bg = {
    emerald: "bg-emerald-50 border-emerald-200 text-emerald-800",
    blue: "bg-blue-50 border-blue-200 text-blue-800",
    amber: "bg-amber-50 border-amber-200 text-amber-800",
    neutral: "bg-white border-[#EAE5D9]",
  }[tone];
  return (
    <div className={`border rounded-lg p-3 ${bg}`}>
      <div className="flex items-center gap-1 text-[10px] uppercase tracking-wider">
        {Icon && <Icon className="w-3.5 h-3.5" />} {label}
      </div>
      <div className="text-2xl font-[Outfit] font-semibold mt-0.5">{value}</div>
    </div>
  );
}

function ShowroomCard({ s, onEdit, onToggle, onDelete, onCatalog, onTeam }) {
  return (
    <div
      className={`bg-white border rounded-lg overflow-hidden ${s.is_active ? "border-[#EAE5D9]" : "border-slate-200 opacity-70"}`}
      data-testid={`sr-card-${s.id}`}
    >
      <div className="p-4 border-b border-[#EAE5D9] bg-gradient-to-br from-[#FEF9EA] to-white">
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0">
            <div className="flex items-center gap-2 text-[10px] uppercase tracking-wider text-[#8B7355]">
              <span className="font-mono text-[#C05621]">{s.store_code || "—"}</span>
              <span>·</span>
              <span>{s.code}</span>
            </div>
            <div className="font-[Outfit] text-lg font-semibold truncate" data-testid={`sr-name-${s.id}`}>
              {s.name}
            </div>
          </div>
          <div className="flex items-center gap-1.5">
            {s.ownership_type === "franchisee" ? (
              <Badge className="border-0 bg-[#C05621] text-white" data-testid={`sr-ownership-${s.id}`}>Franchisee</Badge>
            ) : (
              <Badge variant="outline" className="border-[#C8B8A0] text-[#5C4033]" data-testid={`sr-ownership-${s.id}`}>Owned</Badge>
            )}
            {s.is_active ? (
              <Badge className="border-0 bg-emerald-500 text-white">Active</Badge>
            ) : (
              <Badge className="border-0 bg-slate-400 text-white">Inactive</Badge>
            )}
          </div>
        </div>
      </div>

      <div className="p-4 space-y-2 text-xs">
        {s.ownership_type === "franchisee" && (
          <div className="flex items-center gap-2 text-[#C05621] font-medium" data-testid={`sr-split-${s.id}`}>
            <IndianRupee className="w-3 h-3 flex-shrink-0" />
            <div>{s.franchisee_pct ?? 70}% Franchisee / {s.owner_pct ?? 30}% Owner (HO) revenue split applies</div>
          </div>
        )}
        {(s.city || s.state) && (
          <div className="flex items-start gap-2 text-[#5C4033]">
            <MapPin className="w-3 h-3 mt-0.5 flex-shrink-0" />
            <div className="truncate">{[s.address_line1, s.city, s.state].filter(Boolean).join(" · ")}</div>
          </div>
        )}
        {s.store_manager_name && (
          <div className="flex items-center gap-2 text-[#5C4033]">
            <User className="w-3 h-3 flex-shrink-0" />
            <div className="truncate">
              {s.store_manager_name}{s.store_manager_phone ? ` · ${s.store_manager_phone}` : ""}
            </div>
          </div>
        )}
        {s.opening_hours && (
          <div className="flex items-center gap-2 text-[#5C4033]">
            <Clock className="w-3 h-3 flex-shrink-0" />
            <div>{s.opening_hours}</div>
          </div>
        )}
        {s.default_gst_rate != null && (
          <div className="flex items-center gap-2 text-[#5C4033]">
            <Percent className="w-3 h-3 flex-shrink-0" />
            <div>{s.default_gst_rate}% GST (retail)</div>
          </div>
        )}
        <div className="flex items-center gap-4 pt-2 text-[10px] text-[#8B7355] border-t border-dashed border-[#EAE5D9]">
          <span><b className="text-[#5C4033]">{s.users_count}</b> staff</span>
          <span><b className="text-[#5C4033]">{s.today_sales_count}</b> bills today</span>
          <span><b className="text-emerald-700">₹{Number(s.today_sales_amount || 0).toLocaleString(undefined, { maximumFractionDigits: 0 })}</b></span>
        </div>
      </div>

      <div className="p-3 pt-0 flex gap-1.5 flex-wrap">
        <Button size="sm" variant="outline" onClick={onCatalog} className="flex-1"
          data-testid={`sr-catalog-${s.id}`}>
          <Package className="w-3 h-3 mr-1" /> Catalog
        </Button>
        <Button size="sm" variant="outline" onClick={onTeam} className="flex-1"
          data-testid={`sr-team-${s.id}`}>
          <User className="w-3 h-3 mr-1" /> Team
        </Button>
        <Button size="sm" variant="outline" onClick={onEdit} data-testid={`sr-edit-${s.id}`}>
          <Pencil className="w-3 h-3 mr-1" /> Edit
        </Button>
        <Button size="sm" variant="outline" onClick={onToggle} data-testid={`sr-toggle-${s.id}`}>
          {s.is_active ? "Off" : "On"}
        </Button>
        <Button
          size="sm"
          variant="outline"
          onClick={onDelete}
          className="text-red-600 hover:bg-red-50 hover:border-red-300"
          data-testid={`sr-delete-${s.id}`}
        >
          <Trash2 className="w-3 h-3" />
        </Button>
      </div>
    </div>
  );
}

// ==================== Create / Edit Dialog ====================
function ShowroomFormDialog({ initial, onClose, onSaved }) {
  const isEdit = !!initial;
  const [form, setForm] = useState(() =>
    isEdit ? { ...emptyForm, ...initial, auto_create_user: false } : { ...emptyForm }
  );
  const [busy, setBusy] = useState(false);
  const set = (patch) => setForm((prev) => ({ ...prev, ...patch }));
  const { user } = useAuth();
  const isSuper = (user?.role_id || user?.role) === "super_admin";
  const splitError = (() => {
    if (form.ownership_type !== "franchisee") return "";
    const o = form.owner_pct, f = form.franchisee_pct;
    if (o === "" || o == null || f === "" || f == null) return "Enter both Owner % and Franchisee %";
    if (Number(o) < 0 || Number(f) < 0 || Number(o) > 100 || Number(f) > 100) return "Each share must be between 0% and 100%";
    const sum = round2(Number(o) + Number(f));
    return sum === 100 ? "" : `Owner % + Franchisee % must add up to 100% (currently ${sum}%)`;
  })();

  const save = async () => {
    if (!form.code?.trim() || !form.name?.trim()) {
      toast.error("Code and name are required");
      return;
    }
    if (splitError) { toast.error(splitError); return; }
    setBusy(true);
    try {
      const payload = {
        code: form.code.trim().toUpperCase(),
        name: form.name.trim(),
        address_line1: form.address_line1 || null,
        address_line2: form.address_line2 || null,
        city: form.city || null,
        state: form.state || null,
        pincode: form.pincode || null,
        gstin: form.gstin || null,
        phone: form.phone || null,
        email: form.email || null,
        store_manager_name: form.store_manager_name || null,
        store_manager_phone: form.store_manager_phone || null,
        opening_hours: form.opening_hours || null,
        pos_terminal_count: form.pos_terminal_count ? Number(form.pos_terminal_count) : null,
        default_gst_rate: form.default_gst_rate !== "" && form.default_gst_rate != null ? Number(form.default_gst_rate) : null,
        location_lat: form.location_lat ? Number(form.location_lat) : null,
        location_lng: form.location_lng ? Number(form.location_lng) : null,
        notes: form.notes || null,
        is_active: !!form.is_active,
        ownership_type: form.ownership_type || "owned",
        ...(form.ownership_type === "franchisee" ? { owner_pct: Number(form.owner_pct), franchisee_pct: Number(form.franchisee_pct) } : {}),
      };
      if (isEdit) {
        const r = await api.patch(`/showrooms/${initial.id}`, payload);
        toast.success("Saved");
        onSaved({ showroom: r.data });
        return;
      }
      // 1️⃣ Create the showroom (no auto_create_user — we handle team in step 2)
      const r = await api.post("/showrooms", payload);
      const showroom = r.data.showroom || r.data;
      toast.success(`Showroom ${showroom.store_code || showroom.code} created`);

      // 2️⃣ Sequentially create staff (Admin / Sales Manager / Cashier)
      const createdTeam = [];
      for (const staff of (form.team || [])) {
        if (!staff.enabled) continue;
        if (!staff.name?.trim() || !staff.email?.trim()) {
          toast.error(`Skipped ${staff.role_label} — name & email required`);
          continue;
        }
        try {
          const body = {
            name: staff.name.trim(),
            email: staff.email.trim().toLowerCase(),
            role: staff.role,
            phone: staff.phone || undefined,
          };
          if (staff.password?.trim()) body.password = staff.password.trim();
          const t = await api.post(`/showrooms/${showroom.id}/team`, body);
          createdTeam.push({
            role: staff.role,
            role_label: staff.role_label,
            email: t.data.user.email,
            name: t.data.user.name,
            password: t.data.temp_password || staff.password?.trim(),
            auto_generated: !!t.data.temp_password,
          });
        } catch (e) {
          toast.error(`Failed to create ${staff.role_label}: ${e?.response?.data?.detail || "error"}`);
        }
      }

      onSaved({
        showroom,
        team: createdTeam,
        multi_creds: createdTeam.length > 0 ? createdTeam : null,
      });
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={true} onOpenChange={onClose}>
      <DialogContent className="bg-white max-w-3xl max-h-[92vh] overflow-y-auto" data-testid="sr-form-dialog">
        <DialogHeader>
          <DialogTitle className="font-[Outfit] flex items-center gap-2">
            <Store className="w-5 h-5" /> {isEdit ? `Edit — ${initial.name}` : "New Retail Showroom"}
          </DialogTitle>
          <DialogDescription className="text-[11px] text-[#8B7355]">
            Set up the store profile. Only <b>Code</b> and <b>Name</b> are required; everything else can be filled later.
          </DialogDescription>
        </DialogHeader>

        <div className="grid grid-cols-2 gap-3 pt-2">
          <div>
            <Label>Showroom Code*</Label>
            <Input value={form.code} onChange={(e) => set({ code: e.target.value })} placeholder="STR-01" disabled={isEdit && !!initial.code} data-testid="sr-form-code" />
          </div>
          <div>
            <Label>Showroom Name*</Label>
            <Input value={form.name} onChange={(e) => set({ name: e.target.value })} placeholder="Bakemill Foods — Andheri" data-testid="sr-form-name" />
          </div>

          <div className="col-span-2"><div className="uc-label !text-[#5C4033] mt-3">Address</div></div>
          <div className="col-span-2">
            <Label>Address line 1</Label>
            <Input value={form.address_line1 || ""} onChange={(e) => set({ address_line1: e.target.value })} data-testid="sr-form-addr1" />
          </div>
          <div className="col-span-2">
            <Label>Address line 2</Label>
            <Input value={form.address_line2 || ""} onChange={(e) => set({ address_line2: e.target.value })} data-testid="sr-form-addr2" />
          </div>
          <div><Label>City</Label><Input value={form.city || ""} onChange={(e) => set({ city: e.target.value })} data-testid="sr-form-city" /></div>
          <div><Label>State</Label><Input value={form.state || ""} onChange={(e) => set({ state: e.target.value })} data-testid="sr-form-state" /></div>
          <div><Label>Pincode</Label><Input value={form.pincode || ""} onChange={(e) => set({ pincode: e.target.value })} data-testid="sr-form-pincode" /></div>
          <div><Label>GSTIN</Label><Input value={form.gstin || ""} onChange={(e) => set({ gstin: e.target.value })} data-testid="sr-form-gstin" /></div>

          <div className="col-span-2"><div className="uc-label !text-[#5C4033] mt-3">Contact & Staff</div></div>
          <div><Label>Store phone</Label><Input value={form.phone || ""} onChange={(e) => set({ phone: e.target.value })} data-testid="sr-form-phone" /></div>
          <div><Label>Store email</Label><Input type="email" value={form.email || ""} onChange={(e) => set({ email: e.target.value })} data-testid="sr-form-email" /></div>
          <div><Label>Store manager name</Label><Input value={form.store_manager_name || ""} onChange={(e) => set({ store_manager_name: e.target.value })} data-testid="sr-form-manager" /></div>
          <div><Label>Store manager phone</Label><Input value={form.store_manager_phone || ""} onChange={(e) => set({ store_manager_phone: e.target.value })} data-testid="sr-form-manager-phone" /></div>

          <div className="col-span-2"><div className="uc-label !text-[#5C4033] mt-3">Operations</div></div>
          <div>
            <Label>Opening hours</Label>
            <Input value={form.opening_hours || ""} onChange={(e) => set({ opening_hours: e.target.value })} placeholder="9:00 AM – 10:00 PM" data-testid="sr-form-hours" />
          </div>
          <div>
            <Label>POS terminals</Label>
            <Input type="number" min="0" value={form.pos_terminal_count || ""} onChange={(e) => set({ pos_terminal_count: e.target.value })} data-testid="sr-form-terminals" />
          </div>
          <div>
            <Label>Default GST% (retail)</Label>
            <Input type="number" min="0" step="0.5" value={form.default_gst_rate ?? 5} onChange={(e) => set({ default_gst_rate: e.target.value })} data-testid="sr-form-gst" />
          </div>
          <div>
            <Label>Ownership Type</Label>
            <Select value={form.ownership_type || "owned"} onValueChange={(v) => set({ ownership_type: v })}>
              <SelectTrigger data-testid="sr-form-ownership-type"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-white">
                <SelectItem value="owned">Owned</SelectItem>
                <SelectItem value="franchisee">Franchisee (revenue split)</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {form.ownership_type === "franchisee" && (
            <div className="col-span-2 rounded-lg border border-[#C05621]/30 bg-[#FEEBC8]/30 p-3" data-testid="sr-form-franchise">
              <div className="uc-label mb-2 flex items-center gap-1.5"><IndianRupee className="w-3 h-3" /> Franchise revenue share (on net sale, excl. GST)</div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Owner (HO) %</Label>
                  <Input type="number" min="0" max="100" step="0.5" value={form.owner_pct ?? ""} disabled={!isSuper}
                         onChange={(e) => set({ owner_pct: e.target.value })}
                         data-testid="sr-form-owner-pct" />
                </div>
                <div>
                  <Label>Franchisee %</Label>
                  <Input type="number" min="0" max="100" step="0.5" value={form.franchisee_pct ?? ""} disabled={!isSuper}
                         onChange={(e) => set({ franchisee_pct: e.target.value })}
                         data-testid="sr-form-franchisee-pct" />
                </div>
              </div>
              {splitError ? (
                <div className="mt-2 text-xs font-medium text-red-600" data-testid="sr-form-split-error">{splitError}</div>
              ) : (
                <div className="mt-2 text-xs text-[#8B7355]">{isSuper ? "Both fields must add up to 100%. Applies to new bills from the moment you save." : "Only the HO Super Admin can change the revenue share."}</div>
              )}
            </div>
          )}

          <div className="col-span-2">
            <Label>Notes</Label>
            <Textarea rows={2} value={form.notes || ""} onChange={(e) => set({ notes: e.target.value })} data-testid="sr-form-notes" />
          </div>

          <div className="col-span-2 flex items-center gap-3 bg-[#FEF9EA] border border-amber-200 rounded p-3">
            <Switch checked={!!form.is_active} onCheckedChange={(v) => set({ is_active: v })} data-testid="sr-form-active" />
            <div className="text-sm">{form.is_active ? "Showroom is active" : "Showroom is inactive (hidden from POS)"}</div>
          </div>

          {!isEdit && (
            <div className="col-span-2 bg-emerald-50 border border-emerald-200 rounded p-3 space-y-3">
              <div className="flex items-center gap-2">
                <KeyRound className="w-4 h-4 text-emerald-700" />
                <div className="font-medium text-sm">Create staff logins (optional)</div>
                <div className="text-[11px] text-[#5C4033]">
                  — enable up to 3 accounts (Admin / Sales Manager / Cashier). Temp passwords shown once.
                </div>
              </div>
              {form.team.map((s, idx) => (
                <div
                  key={s.role}
                  data-testid={`sr-form-team-${s.role}`}
                  className={`border rounded-md p-3 ${s.enabled ? "border-emerald-300 bg-white" : "border-[#EAE5D9] bg-[#FDFBF7] opacity-90"}`}
                >
                  <label className="flex items-center gap-2 cursor-pointer">
                    <Switch
                      checked={!!s.enabled}
                      onCheckedChange={(v) => {
                        const team = [...form.team];
                        team[idx] = { ...team[idx], enabled: v };
                        set({ team });
                      }}
                      data-testid={`sr-form-team-enable-${s.role}`}
                    />
                    <div className="flex-1">
                      <div className="font-medium text-sm">{s.role_label}</div>
                      <div className="text-[11px] text-[#5C4033]">
                        {s.role === "showroom_admin" && "Full store control + team management + discount override"}
                        {s.role === "showroom_sales_manager" && "POS + Stock + GRN (no discount override)"}
                        {s.role === "cashier" && "POS billing only"}
                      </div>
                    </div>
                  </label>
                  {s.enabled && (
                    <div className="grid grid-cols-2 gap-2 pt-2 pl-9">
                      <div>
                        <Label className="text-[10px]">Full name *</Label>
                        <Input
                          value={s.name}
                          onChange={(e) => {
                            const team = [...form.team];
                            team[idx] = { ...team[idx], name: e.target.value };
                            set({ team });
                          }}
                          placeholder="e.g. Anita Rao"
                          data-testid={`sr-form-team-name-${s.role}`}
                        />
                      </div>
                      <div>
                        <Label className="text-[10px]">Email (username) *</Label>
                        <Input
                          type="email"
                          value={s.email}
                          onChange={(e) => {
                            const team = [...form.team];
                            team[idx] = { ...team[idx], email: e.target.value };
                            set({ team });
                          }}
                          placeholder="anita@bakemill.com"
                          data-testid={`sr-form-team-email-${s.role}`}
                        />
                      </div>
                      <div>
                        <Label className="text-[10px]">Phone</Label>
                        <Input
                          value={s.phone}
                          onChange={(e) => {
                            const team = [...form.team];
                            team[idx] = { ...team[idx], phone: e.target.value };
                            set({ team });
                          }}
                          placeholder="+91-98…"
                          data-testid={`sr-form-team-phone-${s.role}`}
                        />
                      </div>
                      <div>
                        <Label className="text-[10px]">Password <span className="text-[#8B7355]">(blank = auto-generate)</span></Label>
                        <Input
                          type="text"
                          value={s.password}
                          onChange={(e) => {
                            const team = [...form.team];
                            team[idx] = { ...team[idx], password: e.target.value };
                            set({ team });
                          }}
                          placeholder="Leave blank to auto-generate"
                          data-testid={`sr-form-team-password-${s.role}`}
                        />
                      </div>
                    </div>
                  )}
                </div>
              ))}
              <div className="text-[11px] text-[#5C4033] pl-1 flex items-start gap-1.5">
                <ShieldCheck className="w-3.5 h-3.5 flex-shrink-0 mt-0.5 text-emerald-700" />
                Each enabled staff is auto-provisioned scoped to this showroom. If you provide a password, that
                password is used as-is. If blank, one will be auto-generated and shown to you at the end.
              </div>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button
            onClick={save}
            disabled={busy || !!splitError}
            className="bg-[#2C1810] text-white"
            data-testid="sr-form-save"
          >
            <Save className="w-3.5 h-3.5 mr-1" /> {isEdit ? "Save changes" : "Create Showroom"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ==================== Show new user credentials once ====================
function NewUserCredsDialog({ creds, onClose }) {
  const copy = (val, label) => {
    navigator.clipboard?.writeText(val);
    toast.success(`${label} copied`);
  };

  // ---------- MULTI-USER (wizard-style) ----------
  if (creds.multi) {
    const copyAll = () => {
      const lines = [
        `Store: ${creds.showroom_name} (${creds.store_code || ""})`,
        "",
        ...creds.team.map((t) =>
          `• ${t.role_label} — ${t.name}\n   Email: ${t.email}\n   Password: ${t.password}`
        ),
      ];
      navigator.clipboard?.writeText(lines.join("\n"));
      toast.success("All credentials copied");
    };
    return (
      <Dialog open={true} onOpenChange={onClose}>
        <DialogContent className="bg-white max-w-2xl" data-testid="sr-multi-creds-dialog">
          <DialogHeader>
            <DialogTitle className="font-[Outfit] flex items-center gap-2 text-emerald-800">
              <ShieldCheck className="w-5 h-5" />
              Store &amp; Team Ready
              {creds.store_code && (
                <Badge className="ml-1 bg-[#C05621] text-white font-mono border-0">
                  {creds.store_code}
                </Badge>
              )}
            </DialogTitle>
            <DialogDescription>
              <b>{creds.showroom_name}</b> has been created along with{" "}
              <b>{creds.team.length} staff account{creds.team.length === 1 ? "" : "s"}</b>.
              Share these credentials securely — auto-generated passwords will not be shown again.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-2 py-2">
            {creds.team.map((t, i) => (
              <div
                key={i}
                data-testid={`sr-multi-creds-row-${t.role}`}
                className="border border-[#EAE5D9] rounded-md p-3 bg-[#FDFBF7]"
              >
                <div className="flex items-center justify-between gap-2 mb-2">
                  <div>
                    <div className="uc-label">{t.role_label}</div>
                    <div className="text-sm font-medium text-[#2C1810]">{t.name}</div>
                  </div>
                  {t.auto_generated ? (
                    <Badge className="bg-amber-100 text-amber-800 border border-amber-200">
                      Auto-generated password
                    </Badge>
                  ) : (
                    <Badge className="bg-emerald-100 text-emerald-800 border border-emerald-200">
                      Custom password
                    </Badge>
                  )}
                </div>
                <div className="grid grid-cols-2 gap-2 text-xs">
                  <div>
                    <Label className="text-[10px] uppercase text-[#8B7355]">Email</Label>
                    <div className="flex items-center gap-1">
                      <Input value={t.email} readOnly className="font-mono text-xs h-8" />
                      <Button size="sm" variant="outline" onClick={() => copy(t.email, "Email")} className="h-8 px-2">
                        <Copy className="w-3 h-3" />
                      </Button>
                    </div>
                  </div>
                  <div>
                    <Label className="text-[10px] uppercase text-[#8B7355]">Password</Label>
                    <div className="flex items-center gap-1">
                      <Input value={t.password || "(not shown — you set your own)"} readOnly className="font-mono text-xs h-8" />
                      <Button size="sm" variant="outline" onClick={() => copy(t.password || "", "Password")} className="h-8 px-2">
                        <Copy className="w-3 h-3" />
                      </Button>
                    </div>
                  </div>
                </div>
              </div>
            ))}
            <div className="text-[11px] text-amber-800 bg-amber-50 border border-amber-200 rounded p-2 flex items-start gap-2">
              <ShieldCheck className="w-4 h-4 flex-shrink-0 mt-0.5" />
              <div>
                This is the only time these passwords are shown. Store them somewhere safe or copy all at once.
                Users must change auto-generated passwords on first login.
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={copyAll} data-testid="sr-multi-creds-copy-all">
              <Copy className="w-3.5 h-3.5 mr-1" /> Copy all
            </Button>
            <Button
              onClick={onClose}
              className="bg-[#2C1810] text-white"
              data-testid="sr-multi-creds-close"
            >
              <X className="w-3.5 h-3.5 mr-1" /> I've saved these
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    );
  }

  // ---------- SINGLE USER (legacy path — team assignment from Team dialog) ----------
  const roleLabel = creds.role === "cashier" ? "Cashier"
                  : creds.role === "showroom_admin" ? "Showroom Admin"
                  : creds.role === "showroom_sales_manager" ? "Sales Manager"
                  : "Store Manager";
  return (
    <Dialog open={true} onOpenChange={onClose}>
      <DialogContent className="bg-white max-w-md" data-testid="sr-new-user-creds">
        <DialogHeader>
          <DialogTitle className="font-[Outfit] flex items-center gap-2 text-emerald-800">
            <ShieldCheck className="w-5 h-5" /> {roleLabel} Ready
          </DialogTitle>
          <DialogDescription>
            A <b>{roleLabel}</b> login for <b>{creds.showroom_name}</b> has been created. Share these credentials
            securely — the temporary password will not be shown again. The user will be prompted to change it on
            first login.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3 py-2">
          <div>
            <Label className="text-[10px] uppercase text-[#8B7355]">Email</Label>
            <div className="flex items-center gap-1">
              <Input value={creds.email} readOnly className="font-mono text-sm" data-testid="sr-creds-email" />
              <Button size="sm" variant="outline" onClick={() => copy(creds.email, "Email")}>
                <Copy className="w-3.5 h-3.5" />
              </Button>
            </div>
          </div>
          <div>
            <Label className="text-[10px] uppercase text-[#8B7355]">Temporary Password</Label>
            <div className="flex items-center gap-1">
              <Input value={creds.password} readOnly className="font-mono text-sm" data-testid="sr-creds-password" />
              <Button size="sm" variant="outline" onClick={() => copy(creds.password, "Password")}>
                <Copy className="w-3.5 h-3.5" />
              </Button>
            </div>
          </div>
          <div className="text-[11px] text-amber-800 bg-amber-50 border border-amber-200 rounded p-2 flex items-start gap-2">
            <ShieldCheck className="w-4 h-4 flex-shrink-0 mt-0.5" />
            <div>
              Store these somewhere safe. This is the only time the temporary password is displayed.
              You can reset it later from the Users page.
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button
            onClick={onClose}
            className="bg-[#2C1810] text-white"
            data-testid="sr-creds-close"
          >
            <X className="w-3.5 h-3.5 mr-1" /> I've saved these
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ==================== Per-Showroom Catalog Dialog ====================
function ShowroomCatalogDialog({ showroom, onClose }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [assignedOnly, setAssignedOnly] = useState(false);
  const [dirty, setDirty] = useState({}); // {product_id: {available, price}}

  const load = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (search.trim()) params.set("search", search.trim());
      if (assignedOnly) params.set("only_assigned", "true");
      const r = await api.get(`/showrooms/${showroom.id}/products${params.toString() ? `?${params.toString()}` : ""}`);
      setRows(r.data);
      setDirty({});
    } catch (e) { toast.error(e?.response?.data?.detail || "Load failed"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [assignedOnly]);

  const patchOne = (pid, patch) => {
    setDirty((d) => ({ ...d, [pid]: { ...(d[pid] || {}), ...patch } }));
  };

  const rowValues = (p) => {
    const d = dirty[p.id] || {};
    return {
      available: d.available !== undefined ? d.available : p.showroom_available,
      price: d.price !== undefined ? d.price : p.showroom_price,
    };
  };

  const dirtyCount = Object.keys(dirty).length;

  const saveAll = async () => {
    const items = Object.entries(dirty).map(([pid, v]) => ({
      product_id: pid,
      available: v.available !== undefined ? v.available : true,
      price: v.price === "" || v.price === null || v.price === undefined ? null : Number(v.price),
    }));
    if (!items.length) return;
    try {
      const r = await api.post(`/showrooms/${showroom.id}/products/bulk`, { items });
      toast.success(`Updated ${r.data.updated} / ${r.data.total} product(s)`);
      setDirty({});
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
  };

  const enableAllFiltered = () => {
    const next = { ...dirty };
    rows.forEach((p) => { if (!rowValues(p).available) next[p.id] = { ...(next[p.id] || {}), available: true }; });
    setDirty(next);
    toast.info("Marked visible — click Save to apply");
  };

  const disableAllFiltered = () => {
    const next = { ...dirty };
    rows.forEach((p) => { if (rowValues(p).available) next[p.id] = { ...(next[p.id] || {}), available: false }; });
    setDirty(next);
    toast.info("Marked hidden — click Save to apply");
  };

  return (
    <Dialog open={true} onOpenChange={onClose}>
      <DialogContent className="bg-white max-w-4xl max-h-[92vh] overflow-hidden flex flex-col" data-testid="sr-catalog-dialog">
        <DialogHeader>
          <DialogTitle className="font-[Outfit] flex items-center gap-2">
            <Package className="w-5 h-5" /> Catalog — {showroom.name}
          </DialogTitle>
          <DialogDescription className="text-[11px] text-[#8B7355]">
            Toggle which products are sold at this showroom and override the retail selling price.
            Leave price empty to use the base MRP. Products with no override behave as available in every showroom.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={(e) => { e.preventDefault(); load(); }} className="flex items-end gap-2 pt-2">
          <div className="flex-1">
            <Label className="text-[10px] uppercase text-[#8B7355]">Search</Label>
            <div className="relative">
              <Search className="w-3.5 h-3.5 absolute left-2 top-2.5 text-[#8B7355]" />
              <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Name or SKU…" className="pl-7" data-testid="sr-catalog-search" />
            </div>
          </div>
          <label className="flex items-center gap-2 text-xs px-2 py-2 cursor-pointer">
            <Switch checked={assignedOnly} onCheckedChange={setAssignedOnly} data-testid="sr-catalog-assigned-only" />
            Assigned only
          </label>
          <Button type="submit" variant="outline" size="sm">Search</Button>
          <Button type="button" variant="outline" size="sm" onClick={enableAllFiltered} data-testid="sr-catalog-enable-all">Mark all visible</Button>
          <Button type="button" variant="outline" size="sm" onClick={disableAllFiltered} data-testid="sr-catalog-disable-all">Mark all hidden</Button>
        </form>

        {/* Table */}
        <div className="flex-1 overflow-y-auto border border-[#EAE5D9] rounded mt-3" data-testid="sr-catalog-list">
          <table className="w-full text-xs">
            <thead className="bg-[#FEF9EA] text-[#8B7355] uppercase text-[10px] tracking-wider sticky top-0">
              <tr>
                <th className="text-left p-2">Product</th>
                <th className="text-left p-2 w-24">Category</th>
                <th className="text-right p-2 w-20">Base MRP</th>
                <th className="text-right p-2 w-28">Showroom Price</th>
                <th className="text-center p-2 w-24">Visible in POS</th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {loading && <tr><td colSpan="5" className="p-6 text-center text-[#8B7355]">Loading…</td></tr>}
              {!loading && rows.length === 0 && (
                <tr><td colSpan="5" className="p-6 text-center text-[#8B7355]">No products match.</td></tr>
              )}
              {rows.map((p) => {
                const v = rowValues(p);
                const isDirty = !!dirty[p.id];
                return (
                  <tr key={p.id} data-testid={`sr-catalog-row-${p.id}`}
                    className={isDirty ? "bg-amber-50" : ""}>
                    <td className="p-2">
                      <div className="font-medium text-[#5C4033]">{p.name}</div>
                      <div className="text-[10px] text-[#8B7355]">{p.sku}</div>
                    </td>
                    <td className="p-2 text-[#5C4033]">{p.category || "—"}</td>
                    <td className="p-2 text-right text-[#5C4033]">₹{Number(p.mrp || 0).toFixed(2)}</td>
                    <td className="p-2 text-right">
                      <Input
                        type="number" min="0" step="0.5"
                        value={v.price ?? ""}
                        placeholder={`₹${Number(p.mrp || 0).toFixed(2)}`}
                        onChange={(e) => patchOne(p.id, { price: e.target.value === "" ? null : e.target.value, available: v.available })}
                        className="h-7 text-right w-24 ml-auto"
                        data-testid={`sr-catalog-price-${p.id}`}
                      />
                    </td>
                    <td className="p-2 text-center">
                      <Switch
                        checked={!!v.available}
                        onCheckedChange={(val) => patchOne(p.id, { available: val })}
                        data-testid={`sr-catalog-toggle-${p.id}`}
                      />
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        <DialogFooter className="pt-3">
          <div className="text-xs text-[#8B7355] mr-auto">
            {dirtyCount > 0 ? <span className="text-amber-700 font-medium">{dirtyCount} unsaved change{dirtyCount === 1 ? "" : "s"}</span> : "No changes"}
          </div>
          <Button variant="outline" onClick={onClose}>Close</Button>
          <Button
            onClick={saveAll}
            disabled={dirtyCount === 0}
            className="bg-[#C05621] text-white hover:bg-[#a04519]"
            data-testid="sr-catalog-save"
          >
            <Save className="w-3.5 h-3.5 mr-1" /> Save {dirtyCount || ""}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


// ============================ TEAM DIALOG ============================

const _TEAM_ROLE_OPTS = [
  { value: "showroom_admin", label: "Showroom Admin", desc: "Full store control incl. team + discounts" },
  { value: "showroom_sales_manager", label: "Sales Manager", desc: "POS + Stock + GRN (no discount override)" },
  { value: "cashier", label: "Cashier", desc: "POS billing only" },
  { value: "branch_manager", label: "Branch Manager (legacy)", desc: "Old-role equivalent of Showroom Admin" },
];

function ShowroomTeamDialog({ showroom, onClose, onCredentials, onChanged }) {
  const [team, setTeam] = useState([]);
  const [loading, setLoading] = useState(true);
  const [addOpen, setAddOpen] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", role: "showroom_admin", phone: "" });
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/showrooms/${showroom.id}/team`);
      setTeam(data || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load team");
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [showroom.id]);

  const addMember = async () => {
    if (!form.name || !form.email || !form.role) { toast.error("Name, email and role are required"); return; }
    setSaving(true);
    try {
      const { data } = await api.post(`/showrooms/${showroom.id}/team`, form);
      toast.success(`${data.user.name} added to ${showroom.name}`);
      if (data.temp_password && onCredentials) {
        onCredentials({
          showroom_name: showroom.name,
          email: data.user.email,
          password: data.temp_password,
          name: data.user.name,
          role: data.user.role,
        });
      }
      setAddOpen(false);
      setForm({ name: "", email: "", role: "showroom_admin", phone: "" });
      await load();
      onChanged?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to add team member");
    } finally { setSaving(false); }
  };

  const unassign = async (u) => {
    if (!window.confirm(`Unassign ${u.name} from ${showroom.name}? Their account will remain but be branch-less.`)) return;
    try {
      await api.delete(`/showrooms/${showroom.id}/team/${u.id}`);
      toast.success(`${u.name} unassigned`);
      await load();
      onChanged?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to unassign");
    }
  };

  return (
    <Dialog open={true} onOpenChange={onClose}>
      <DialogContent className="bg-white max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="sr-team-dialog">
        <DialogHeader>
          <DialogTitle className="font-[Outfit] flex items-center gap-2">
            <User className="w-5 h-5 text-[#C05621]" />
            {showroom.name} — Team
            <span className="text-xs font-mono text-[#C05621] ml-1">{showroom.store_code}</span>
          </DialogTitle>
          <DialogDescription className="text-xs text-[#8B7355]">
            Assign Showroom Admins, Sales Managers, and Cashiers to this store. Assigned staff
            will be locked to this store and can only see its POS, Stock, and GRN.
          </DialogDescription>
        </DialogHeader>

        <div className="flex justify-between items-center pt-1">
          <div className="text-xs text-[#8B7355]">
            <b className="text-[#2C1810]">{team.length}</b> staff assigned
          </div>
          <Button
            size="sm"
            onClick={() => setAddOpen((v) => !v)}
            data-testid="sr-team-add-toggle"
            className="bg-[#C05621] hover:bg-[#9C4221] text-white"
          >
            <Plus className="w-3.5 h-3.5 mr-1" /> {addOpen ? "Close" : "Add member"}
          </Button>
        </div>

        {addOpen && (
          <div className="border border-[#EAE5D9] rounded-md p-3 bg-[#FDFBF7] space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-[10px] uppercase text-[#8B7355]">Name *</Label>
                <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="sr-team-name" />
              </div>
              <div>
                <Label className="text-[10px] uppercase text-[#8B7355]">Email *</Label>
                <Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="sr-team-email" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-[10px] uppercase text-[#8B7355]">Role *</Label>
                <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                  <SelectTrigger data-testid="sr-team-role"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-white">
                    {_TEAM_ROLE_OPTS.map((r) => (
                      <SelectItem key={r.value} value={r.value}>
                        {r.label}<span className="text-[10px] text-[#8B7355] ml-1">— {r.desc}</span>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-[10px] uppercase text-[#8B7355]">Phone</Label>
                <Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="sr-team-phone" placeholder="+91-9…" />
              </div>
            </div>
            <div className="text-[11px] text-[#8B7355]">
              A temporary password will be auto-generated and shown once. The user will be forced to change it on first login.
            </div>
            <div className="flex justify-end">
              <Button onClick={addMember} disabled={saving} data-testid="sr-team-add-submit" className="bg-[#C05621] hover:bg-[#9C4221] text-white">
                {saving ? "Adding…" : "Add member"}
              </Button>
            </div>
          </div>
        )}

        <div className="border border-[#EAE5D9] rounded-md overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left uc-label border-b border-[#EAE5D9] bg-[#FDFBF7]">
                <th className="px-3 py-2">Name</th>
                <th className="px-3 py-2">Email</th>
                <th className="px-3 py-2">Role</th>
                <th className="px-3 py-2">Phone</th>
                <th className="px-3 py-2"></th>
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr><td colSpan={5} className="px-3 py-6 text-center text-[#8B7355]">Loading…</td></tr>
              )}
              {!loading && team.length === 0 && (
                <tr><td colSpan={5} className="px-3 py-6 text-center text-[#8B7355]">
                  No team members yet. Click <b>Add member</b> to assign staff.
                </td></tr>
              )}
              {!loading && team.map((u) => (
                <tr key={u.id} className="border-b border-[#EAE5D9] last:border-0" data-testid={`sr-team-row-${u.id}`}>
                  <td className="px-3 py-2 font-medium text-[#2C1810]">{u.name}</td>
                  <td className="px-3 py-2 text-[12px] text-[#5C4033]">{u.email}</td>
                  <td className="px-3 py-2">
                    <Badge className="border bg-white border-[#EAE5D9] text-[#5C4033]">
                      {(u.role || "").replace(/_/g, " ")}
                    </Badge>
                  </td>
                  <td className="px-3 py-2 text-[12px]">{u.phone || "—"}</td>
                  <td className="px-3 py-2 text-right">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => unassign(u)}
                      data-testid={`sr-team-unassign-${u.id}`}
                      className="border-red-300 text-red-600 hover:bg-red-50"
                    >
                      Unassign
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Close</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
